import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { service } from "@ember/service";
import PluginOutlet from "discourse/components/plugin-outlet";
import DMenu from "discourse/float-kit/components/d-menu";
import lazyHash from "discourse/helpers/lazy-hash";
import DButton from "discourse/ui-kit/d-button";
import DConditionalLoadingSpinner from "discourse/ui-kit/d-conditional-loading-spinner";
import DUserAvatar from "discourse/ui-kit/d-user-avatar";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import PostLikedUsersMenu from "./liked-users-menu";

const LIKE_ACTION = 2; // The action type ID for "like" in Discourse
const DISPLAY_MAX_USERS = 8; // will show X users, then a button to show one more row of X;
const MENU_IDENTIFIER = "post-liked-users-menu";

export default class LikedUsersList extends Component {
  @service menu;
  @service siteSettings;
  @service store;

  @tracked likedUsers;
  @tracked loadingLikedUsers = false;
  @tracked slicedUsersVisible = false;

  get useNewMenu() {
    return this.siteSettings.enable_new_post_reactions_menu;
  }

  get buttonIcon() {
    return this.args.post.yours ? "d-liked" : null;
  }

  @action
  togglePopup(event) {
    const target = event.currentTarget;
    const virtualElement = {
      getBoundingClientRect: () => target.getBoundingClientRect(),
    };

    this.menu.show(virtualElement, {
      identifier: MENU_IDENTIFIER,
      component: PostLikedUsersMenu,
      modalForMobile: true,
      closeOnScroll: true,
      arrow: true,
      placement: "bottom",
      offset: 15,
      data: { post: this.args.post },
    });
  }

  @action
  async fetchLikedUsers() {
    if (this.loadingLikedUsers) {
      return;
    }

    this.loadingLikedUsers = true;

    try {
      this.likedUsers = await this.store.find("post-action-user", {
        id: this.args.post.id,
        post_action_type_id: LIKE_ACTION,
      });
    } finally {
      this.loadingLikedUsers = false;
    }
  }

  @action
  toggleSlicedUsersVisiblity() {
    this.slicedUsersVisible = !this.slicedUsersVisible;
  }

  get icon() {
    if (!this.args.post.showLike) {
      return this.args.post.yours ? "d-liked" : "d-unliked";
    }

    if (this.args.post.yours) {
      return "d-liked";
    }
  }

  get truncatedUsers() {
    return this.likedUsers?.content.slice(0, DISPLAY_MAX_USERS);
  }

  get slicedUsers() {
    return this.likedUsers?.content.slice(
      DISPLAY_MAX_USERS,
      DISPLAY_MAX_USERS * 2
    );
  }

  get hiddenUserCount() {
    return (
      this.likedUsers?.length -
      (this.truncatedUsers.length + this.slicedUsers.length)
    );
  }

  get toggleSlicedUsersVisiblityIcon() {
    return this.slicedUsersVisible ? "angle-up" : "angle-down";
  }

  <template>
    {{#if this.useNewMenu}}
      <button
        type="button"
        aria-label={{i18n
          "post.sr_post_like_count_button"
          count=@post.likeCount
        }}
        class={{dConcatClass
          "btn btn-flat no-text"
          "post-action-menu__like-count"
          "like-count"
          "button-count"
          "highlight-action"
          (if @post.liked "has-liked")
          (if @post.yours "my-likes" "regular-likes")
        }}
        {{on "click" this.togglePopup}}
        ...attributes
      >
        {{#if this.buttonIcon}}
          {{dIcon this.buttonIcon}}
        {{/if}}
        {{@post.likeCount}}
      </button>
    {{else}}
      <DMenu
        @modalForMobile={{true}}
        @identifier="post-like-users_{{@post.id}}"
        @onShow={{this.fetchLikedUsers}}
        @triggerClass={{dConcatClass
          "post-action-menu__like-count"
          "like-count"
          "btn-flat"
          "button-count"
          "highlight-action"
          (if @post.yours "my-likes" "regular-likes")
        }}
        @icon={{if @post.yours "d-liked" ""}}
        @placement="top"
        label={{i18n "post.sr_post_like_count_button" count=@post.likeCount}}
        id="post-like-users_{{@post.id}}"
      >
        <:trigger>
          {{@post.likeCount}}
        </:trigger>
        <:content>
          <DConditionalLoadingSpinner
            @condition={{this.loadingLikedUsers}}
            class="liked-users-list__container"
          >
            <span class="liked-users-list__count">
              {{dIcon "d-liked" class="liked-users-list__count-icon"}}
              {{@post.likeCount}}
            </span>
            <div class="liked-users-list">
              <ul class="liked-users-list__list">
                {{#each this.truncatedUsers as |user|}}
                  <li class="liked-users-list__item">
                    <PluginOutlet
                      @name="liked-users-list-avatar"
                      @outletArgs={{lazyHash user=user post=@post}}
                    >
                      <DUserAvatar
                        class="trigger-user-card"
                        @ariaHidden={{false}}
                        @user={{user}}
                        @size="small"
                      />
                    </PluginOutlet>
                  </li>
                {{/each}}
                {{#if this.slicedUsers}}
                  <li class="liked-users-list__item">
                    <DButton
                      class="liked-users-list__more-button btn-flat"
                      @icon={{this.toggleSlicedUsersVisiblityIcon}}
                      @action={{this.toggleSlicedUsersVisiblity}}
                    />
                  </li>
                {{/if}}
              </ul>
              {{#if this.slicedUsersVisible}}
                <ul class="liked-users-list__list">
                  {{#each this.slicedUsers as |user|}}
                    <li class="liked-users-list__item">
                      <PluginOutlet
                        @name="liked-users-list-avatar"
                        @outletArgs={{lazyHash user=user post=@post}}
                      >
                        <DUserAvatar
                          class="trigger-user-card"
                          @ariaHidden={{false}}
                          @user={{user}}
                          @size="small"
                        />
                      </PluginOutlet>
                    </li>
                  {{/each}}
                </ul>
                {{#if this.hiddenUserCount}}
                  <span class="liked-users-list__more">
                    {{i18n
                      "discourse_reactions.state_panel.more_users"
                      count=this.hiddenUserCount
                    }}
                  </span>
                {{/if}}
              {{/if}}
            </div>
          </DConditionalLoadingSpinner>
        </:content>
      </DMenu>
    {{/if}}
  </template>
}
